import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";

export const NO_DATA = "N/A";

export const STATUS_MAP: Record<string, { label: string; badge: string; header: string; text: string }> = {
  N: { label: "New", badge: "badge bg-primary text-white", header: "#2196F3", text: "#6c757d" },
  T: { label: "To Be Approved", badge: "badge bg-warning text-dark", header: "#FF9800", text: "#FF9800" },
  P: { label: "Partially Approved", badge: "badge bg-warning text-dark", header: "#FF9800", text: "#FF9800" },
  A: { label: "Approved", badge: "badge bg-success text-white", header: "#198754", text: "#198754" },
  R: { label: "Rejected", badge: "badge bg-danger text-white", header: "#dc3545", text: "#dc3545" },
  D: { label: "Processing Complete", badge: "badge bg-success text-white", header: "#198754", text: "#198754" },
  X: { label: "Deleted", badge: "badge bg-secondary text-white", header: "#6c757d", text: "#6c757d" },
  B: { label: "To Be Processed", badge: "badge bg-primary text-white", header: "#2196F3", text: "#6c757d" },
  C: { label: "Closed", badge: "badge bg-secondary text-white", header: "#6c757d", text: "#6c757d" },
  K: { label: "Cancelled", badge: "badge bg-secondary text-white", header: "#6c757d", text: "#6c757d" },
  S: { label: "Settled", badge: "badge bg-success text-white", header: "#198754", text: "#198754" },
};

export const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "N", label: "New" },
  { value: "T", label: "To Be Approved" },
  { value: "P", label: "Partially Approved" },
  { value: "A", label: "Approved" },
  { value: "R", label: "Rejected" },
  { value: "D", label: "Processing Complete" },
  { value: "X", label: "Deleted" },
  { value: "B", label: "To Be Processed" },
  { value: "C", label: "Closed" },
  { value: "K", label: "Cancelled" },
  { value: "S", label: "Settled" },
];

export const REASON_MAP: Record<string, string> = {
  V8: "1) Issue of new SV4 card",
  V2: "2) Loss & Theft SV4",
  V3: "3) Physically Damage unreadable SV4 cards",
  V4: "4) Physical Ok unreadable SV4 card",
  V5: "5) Expiry SV4 card",
  V6: "6) Change in designation/promotion/transfer",
  V7: "7) Correction in SV4 card",
};

export const REASONS_WITHOUT_UPLOAD = new Set(["V5"]);

export const MANDATORY_ATTACHMENT_NOTES = [
  "1. In case of loss/theft, FIR/Loss article copy must be uploaded.",
  "2. In case of SV4 card damage/SV4 card physical ok but unreadable, copy of URC/passenger foil must be uploaded.",
  "3. In case of change in designation/promotion/transfer, related office order copy must be enclosed.",
  "4. In case of correction in SV4 card, copy of existing SV4 card to be uploaded.",
  "5. Passport size photo must be uploaded with all requested applications.",
  "6. Office order copy of joining and passport size photo will be mandatory in case of new requisition of SV4 card.",
];

export const SUCCESS_SUBMIT_MESSAGE =
  "Request Submitted Successfully. Process for issue of new SV4 stickers will take 7-10 working days from the date of approval.";

export const DEDUCTION_NOTE =
  "An amount of Rs. 100 will be deducted from the employee salary in case of SV4 card lost/damaged.";

const ATTACHMENT_ALLOWED_EXT = new Set(["pdf", "jpg", "jpeg"]);
export const PHOTO_MIN_BYTES = 20 * 1024;
export const PHOTO_MAX_BYTES = 50 * 1024;
export const ATTACHMENT_MAX_BYTES = 2 * 1024 * 1024;

export function getStatusInfo(code: string) {
  return (
    STATUS_MAP[code] || {
      label: code || "Unknown",
      badge: "badge bg-secondary text-white",
      header: "#6c757d",
      text: "#6c757d",
    }
  );
}

export function getLoggedInUser(): any {
  try {
    const raw = localStorage.getItem("user");
    if (!raw || raw === "undefined") return {};
    const parsed = JSON.parse(raw);
    return parsed?.data?.user || parsed?.user || parsed || {};
  } catch {
    return {};
  }
}

export function getLoggedInEmpId(): string {
  const user = getLoggedInUser();
  return String(user?.details?.empId || user?.empId || user?.emp_id || "").trim();
}

export function padEmpId(empId = getLoggedInEmpId()): string {
  const raw = String(empId || "").trim();
  if (!raw) return "";
  if (/^\d+$/.test(raw)) return raw.padStart(8, "0");
  return raw;
}

export function getProfileDetails(reduxProfile?: any): any {
  if (reduxProfile) return reduxProfile;
  return getLoggedInUser()?.details || {};
}

export function getEmployeeDisplayName(details?: any): string {
  const d = details || getProfileDetails();
  return (
    [d?.firstname, d?.lsastname || d?.secondname || d?.lastname]
      .filter(Boolean)
      .join(" ")
      .trim() || ""
  );
}

export function unwrap(res: any): any {
  return res?.data ?? res;
}

export function extractList(res: any): any[] {
  const root = unwrap(res);
  const inner = root?.data ?? root;
  if (Array.isArray(inner?.data)) return inner.data;
  if (Array.isArray(inner?.items)) return inner.items;
  if (Array.isArray(inner?.results)) return inner.results;
  if (Array.isArray(inner)) return inner;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root)) return root;
  if (Array.isArray(res)) return res;
  return [];
}

export function extractOne(res: any): any {
  const root = unwrap(res);
  const inner = root?.data ?? root;
  if (Array.isArray(inner?.data)) return inner.data[0] ?? null;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    if (inner.data && typeof inner.data === "object" && !Array.isArray(inner.data)) {
      return inner.data;
    }
    return inner;
  }
  if (root && typeof root === "object" && !Array.isArray(root)) return root;
  return inner ?? root ?? null;
}

export function isApiSuccess(res: any): boolean {
  if (!res) return false;
  if (res.success === false) return false;
  if (typeof res.status === "number" && res.status >= 400) return false;
  if (res.success === true || res.status === 200 || res.status === 201) return true;
  const inner = unwrap(res);
  if (inner?.success === false) return false;
  if (inner?.id || inner?.ReferenceNumber || inner?.SequenceNumber) return true;
  return Boolean(res);
}

export function apiMessage(res: any, fallback: string): string {
  return (
    res?.message ||
    res?.data?.message ||
    unwrap(res)?.message ||
    fallback
  );
}

export function formatSapDateToDMY(ts: any): string {
  if (!ts) return "";
  const s = String(ts).trim();
  const pad = (v: string | number) => String(v).padStart(2, "0");
  const toYYYY = (y: string) => (y.length === 2 ? `20${y}` : y);

  if (s.startsWith("/Date(") && s.endsWith(")/")) {
    const num = parseInt(s.replace(/\D/g, ""), 10);
    if (!isNaN(num)) {
      const d = new Date(num);
      return `${pad(d.getUTCDate())}-${pad(d.getUTCMonth() + 1)}-${d.getUTCFullYear()}`;
    }
    return s;
  }

  let m = s.match(/(\d{4})[-./](\d{1,2})[-./](\d{1,2})/);
  if (m) return `${pad(m[3])}-${pad(m[2])}-${m[1]}`;

  m = s.match(/(\d{1,2})[-./](\d{1,2})[-./](\d{2,4})/);
  if (m) return `${pad(m[1])}-${pad(m[2])}-${toYYYY(m[3])}`;

  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) {
    return `${pad(parsed.getUTCDate())}-${pad(parsed.getUTCMonth() + 1)}-${parsed.getUTCFullYear()}`;
  }
  return s;
}

export function formatSapDateTimeToDMYHM(ts: any): string {
  if (!ts) return "";
  const s = String(ts).trim();
  let d: Date | null = null;
  if (s.startsWith("/Date(") && s.endsWith(")/")) {
    const num = parseInt(s.replace(/\D/g, ""), 10);
    if (!isNaN(num)) d = new Date(num);
  } else if (s.includes("T") || (s.includes("-") && s.length > 8)) {
    const parsed = new Date(s);
    if (!isNaN(parsed.getTime())) d = parsed;
  }
  if (!d) return s;

  const day = String(d.getUTCDate()).padStart(2, "0");
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const year = d.getUTCFullYear();
  let hours = d.getUTCHours();
  const minutes = String(d.getUTCMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${day}-${month}-${year} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
}

export function parseSapDateTimeToMillis(ts: any): number {
  if (!ts) return NaN;
  const s = String(ts).trim();
  if (s.startsWith("/Date(") && s.endsWith(")/")) {
    const num = parseInt(s.replace(/\D/g, ""), 10);
    return isNaN(num) ? NaN : num;
  }
  if (s.includes("T") || (s.includes("-") && s.length > 8)) {
    const parsed = new Date(s);
    return isNaN(parsed.getTime()) ? NaN : parsed.getTime();
  }
  return NaN;
}

export function formatSv4CreationDate(str: string | undefined | null): string {
  if (!str) return "";

  const sapWeirdMatch = str.match(/^(\d{1,2})T[\d:.Z]+\/(\d{2})\/(\d{4})$/);
  if (sapWeirdMatch) {
    const dd = sapWeirdMatch[1].padStart(2, "0");
    return `${dd}/${sapWeirdMatch[2]}/${sapWeirdMatch[3]}`;
  }

  if (str.includes(".")) {
    const parts = str.split(".");
    if (parts.length === 3) return `${parts[0]}/${parts[1]}/${parts[2]}`;
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const parts = str.substring(0, 10).split("-");
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }

  if (str.includes("-")) {
    const parts = str.split("-");
    if (parts.length === 3) return `${parts[0]}/${parts[1]}/${parts[2]}`;
  }

  return str;
}

export function formatSv4DetailTimestamp(iso: string | undefined | null): string {
  if (!iso) return "";
  return formatSapDateToDMY(iso) || iso;
}

export function formatSv4ListItemCreationDisplay(item: any): string {
  const ts = item?.TimeStamp ?? item?.timeStamp;
  if (typeof ts === "string" && ts.trim()) {
    return formatSv4DetailTimestamp(ts) || ts.trim();
  }
  const rc = item?.RequestCreationDate ?? item?.requestCreationDate;
  if (typeof rc === "string" && rc.trim()) {
    return formatSv4DetailTimestamp(rc) || rc.trim();
  }
  const ca = item?.created_at ?? item?.createdAt;
  if (typeof ca === "string" && ca.trim()) {
    return formatSv4DetailTimestamp(ca) || ca.trim();
  }
  return "-";
}

const IST_OFFSET_MIN = 330;

function buildIstDMYHM(dd: string, mm: string, yyyy: string, hh24: number, min: string): string {
  const utcMs = Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd), hh24, Number(min));
  const d = new Date(utcMs - IST_OFFSET_MIN * 60000);
  const day = String(d.getUTCDate()).padStart(2, "0");
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const year = d.getUTCFullYear();
  let hours = d.getUTCHours();
  const minutes = String(d.getUTCMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${day}-${month}-${year} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
}

export function formatRemarkHistoryTimestamp(iso: string | undefined | null): string {
  if (!iso) return "";
  const s = String(iso).trim();
  if (!s) return "";

  const sapWeird = s.match(
    /^(\d{1,2})T(\d{1,2}):(\d{2})(?::\d{2})?(?:\.\d+)?Z?\/(\d{1,2})\/(\d{4})$/,
  );
  if (sapWeird) {
    return buildIstDMYHM(sapWeird[1], sapWeird[4], sapWeird[5], Number(sapWeird[2]), sapWeird[3]);
  }

  const dmy = s.match(
    /^(\d{1,2})[-./](\d{1,2})[-./](\d{2}|\d{4})[ T](\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM|am|pm)?$/,
  );
  if (dmy) {
    const dd = dmy[1];
    const mm = dmy[2];
    const yyyy = dmy[3].length === 2 ? `20${dmy[3]}` : dmy[3];
    let hh = Number(dmy[4]);
    const mer = dmy[6]?.toUpperCase();
    if (mer === "PM" && hh < 12) hh += 12;
    if (mer === "AM" && hh === 12) hh = 0;
    return buildIstDMYHM(dd, mm, yyyy, hh, dmy[5]);
  }

  return formatSapDateTimeToDMYHM(s) || s;
}

export function parseSv4RemarkTimestampMillis(ts: any): number {
  if (!ts) return NaN;
  const s = String(ts).trim();
  if (!s) return NaN;

  const sapWeird = s.match(
    /^(\d{1,2})T(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?Z?\/(\d{1,2})\/(\d{4})$/,
  );
  if (sapWeird) {
    return Date.UTC(
      Number(sapWeird[6]),
      Number(sapWeird[5]) - 1,
      Number(sapWeird[1]),
      Number(sapWeird[2]),
      Number(sapWeird[3]),
      Number(sapWeird[4] ?? 0),
    );
  }

  const dmy = s.match(
    /^(\d{1,2})[-./](\d{1,2})[-./](\d{2}|\d{4})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM|am|pm)?$/,
  );
  if (dmy) {
    const yyyy = dmy[3].length === 2 ? 2000 + Number(dmy[3]) : Number(dmy[3]);
    let hh = Number(dmy[4]);
    const mer = dmy[7]?.toUpperCase();
    if (mer === "PM" && hh < 12) hh += 12;
    if (mer === "AM" && hh === 12) hh = 0;
    return Date.UTC(yyyy, Number(dmy[2]) - 1, Number(dmy[1]), hh, Number(dmy[5]), Number(dmy[6] ?? 0));
  }

  return parseSapDateTimeToMillis(s);
}

export function sortSv4RemarksByTimeStampDesc<T extends { TimeStamp?: any }>(list: T[]): T[] {
  if (!Array.isArray(list)) return [];
  return [...list].sort((a, b) => {
    const ta = parseSv4RemarkTimestampMillis(a?.TimeStamp);
    const tb = parseSv4RemarkTimestampMillis(b?.TimeStamp);
    if (isNaN(ta) && isNaN(tb)) return 0;
    if (isNaN(ta)) return 1;
    if (isNaN(tb)) return -1;
    return tb - ta;
  });
}

function approverNameFromDetailsBlock(d: any): string {
  if (!d || typeof d !== "object") return "";
  const full =
    (typeof d.fullName === "string" && d.fullName.trim()) ||
    (typeof d.FullName === "string" && d.FullName.trim());
  if (full) return full;

  const parts: string[] = [];
  const push = (x: unknown) => {
    if (typeof x === "string" && x.trim()) parts.push(x.trim());
  };
  push(d.firstname);
  push(d.firstName);
  push(d.secondname);
  push(d.secondName);
  push(d.lsastname);
  push(d.lastname);
  push(d.LastName);
  if (parts.length === 0) return "";
  return parts.join(" ").replace(/\s+/g, " ");
}

export function getSv4ApproverNameDisplay(record: any, noData = NO_DATA): string {
  if (!record || typeof record !== "object") return noData;

  const fromRoot = [record.LatestApproverName, record.ApproverName, record.approverName, record.fullName].find(
    (v: any) => typeof v === "string" && v.trim(),
  );
  if (fromRoot) return String(fromRoot).trim();

  const nested = record.approver_details ?? record.approverDetails ?? record.approver_detail;
  const built = approverNameFromDetailsBlock(nested);
  if (built) return built;

  return noData;
}

export function getOrganisationProjDisplay(
  organisationKey: string | undefined | null,
  profileWingName: string | undefined | null,
  noData = NO_DATA,
): string {
  const fromApi = organisationKey?.trim();
  if (fromApi && fromApi.toUpperCase() !== "N/A") return fromApi;
  const wing = profileWingName?.trim();
  return wing || noData;
}

export function stripReasonListPrefix(label: string): string {
  if (!label?.trim()) return label;
  return label.replace(/^\d+\)\s*/, "").trim();
}

export function getReasonDisplayLabel(reasonCode: string, noData = NO_DATA): string {
  const mapped = REASON_MAP[reasonCode];
  const raw = mapped ?? reasonCode;
  if (!raw?.trim()) return noData;
  return stripReasonListPrefix(raw);
}

export function getSv4AttachmentUrl(doc: any): string | undefined {
  const u = doc?.Document ?? doc?.document ?? doc?.url ?? doc?.URL ?? doc?.fileUrl;
  return typeof u === "string" && u.trim() ? u.trim() : undefined;
}

export function getDocumentDisplayName(doc: any, index: number): string {
  const rawName: string = doc?.FileName ?? doc?.fileName ?? `Document_${index + 1}`;
  const displayName = rawName.includes("/") ? rawName.split("/").pop() ?? rawName : rawName;
  return displayName.split("?")[0];
}

export function collectSv4Documents(detail: any): any[] {
  const docResults: any[] = Array.isArray(detail?.ToDocuments?.results)
    ? detail.ToDocuments.results
    : Array.isArray(detail?.ToDocuments)
      ? detail.ToDocuments
      : [];
  const picResults: any[] = Array.isArray(detail?.ToPicture?.results)
    ? detail.ToPicture.results
    : Array.isArray(detail?.ToPicture)
      ? detail.ToPicture
      : [];
  return [...docResults, ...picResults];
}

export function collectSv4Remarks(detail: any): any[] {
  return sortSv4RemarksByTimeStampDesc(
    Array.isArray(detail?.RemarksHistory)
      ? detail.RemarksHistory
      : Array.isArray(detail?.ToRemarks)
        ? detail.ToRemarks
        : [],
  );
}

export function getAttachmentMimeAndKind(file: File): { mime: string; fileType: string } | null {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  const mime = (file.type || "").toLowerCase();
  if (ext === "pdf" || mime.includes("pdf")) {
    return { mime: "application/pdf", fileType: "pdf" };
  }
  if (ext === "jpg" || ext === "jpeg" || mime === "image/jpg" || mime === "image/jpeg" || mime.includes("jpeg")) {
    return { mime: "image/jpeg", fileType: ext === "jpg" || ext === "jpeg" ? ext : "jpg" };
  }
  return null;
}

export function isAllowedAttachment(file: File): boolean {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  const kind = getAttachmentMimeAndKind(file);
  if (!kind) return false;
  if (ext && !ATTACHMENT_ALLOWED_EXT.has(ext)) return false;
  return true;
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

export function toSv4DocResults(
  docs: Array<{ fileName: string; serverKey: string; mime?: string }>,
) {
  return {
    results: docs.map((d) => ({
      FileName: d.fileName,
      Content: d.serverKey ?? "",
    })),
  };
}

export async function uploadSv4File(file: File, empId: string, mimeType: string): Promise<string> {
  const base64 = await fileToBase64(file);
  const res: any = await Api.post(API_URLS.AWS_BUCKET.UPLOAD_FILE, {
    filestring: base64,
    mime_type: mimeType,
    module: `sv4_card/${String(empId)}`,
  });
  const key =
    res?.data?.key ||
    res?.key ||
    unwrap(res)?.key ||
    unwrap(res)?.data?.key ||
    "";
  if (!key) {
    throw new Error(apiMessage(res, "Failed to upload file"));
  }
  return key;
}

export async function getSv4Reasons(): Promise<{ Value: string; Ddtext: string }[]> {
  const res: any = await Api.post(API_URLS.SV4_CARD.GET_REASON, {});
  const root = unwrap(res);
  const inner = root?.data ?? root;
  const list = Array.isArray(inner?.data)
    ? inner.data
    : Array.isArray(inner?.results)
      ? inner.results
      : Array.isArray(inner)
        ? inner
        : Array.isArray(root)
          ? root
          : [];
  return list
    .map((r: any) => ({
      Value: String(r?.Value ?? r?.value ?? r?.KEY ?? r?.key ?? "").trim(),
      Ddtext: String(r?.Ddtext ?? r?.ddtext ?? r?.TEXT ?? r?.text ?? r?.Value ?? "").trim(),
    }))
    .filter((r: { Value: string }) => r.Value);
}

export async function findAllSv4Cards(payload: {
  empId: string;
  page?: number;
  limit?: number;
  status?: string;
  startDate?: string;
  endDate?: string;
}): Promise<any[]> {
  const body: Record<string, any> = {
    page: payload.page ?? 1,
    limit: payload.limit ?? 100,
    search: payload.empId,
  };
  if (payload.status) body.status = payload.status;
  if (payload.startDate && payload.endDate) {
    body.startDate = payload.startDate;
    body.endDate = payload.endDate;
  }
  const res: any = await Api.post(API_URLS.SV4_CARD.FIND_ALL, body);
  if (res?.success === false) {
    throw new Error(apiMessage(res, "Failed to load SV4 card requests"));
  }
  return extractList(res);
}

export async function syncSv4Cards(empId: string): Promise<any> {
  const res: any = await Api.post(API_URLS.SV4_CARD.SYNC, {
    empId: padEmpId(empId),
  });
  if (res?.success === false) {
    throw new Error(apiMessage(res, "Sync failed"));
  }
  return res;
}

export async function findOneSv4Card(id: string): Promise<any> {
  const res: any = await Api.post(API_URLS.SV4_CARD.FIND_ONE, { id });
  if (res?.success === false) {
    throw new Error(apiMessage(res, "Failed to load request details"));
  }
  return extractOne(res);
}

function extractCreateError(error: any): string {
  const data = error?.response?.data || error?.data || error;
  const nested = data?.data?.data || data?.data || data;
  const msg =
    nested?.response?.message ||
    nested?.message ||
    data?.data?.message ||
    data?.message ||
    data?.error;
  if (Array.isArray(msg) && msg.length) {
    return msg.map((m) => (typeof m === "string" ? m : JSON.stringify(m))).join(". ");
  }
  if (typeof msg === "string" && msg.trim() && !/status(?: code)? 422/i.test(msg)) {
    return msg;
  }
  return Api.getErrorMessage(error) || "Failed to submit request";
}

export async function createSv4Card(payload: any): Promise<any> {
  try {
    const res: any = await Api.post(API_URLS.SV4_CARD.CREATE, payload);
    if (!isApiSuccess(res)) {
      throw new Error(apiMessage(res, "Failed to submit request"));
    }
    return res;
  } catch (error: any) {
    if (error?.message && !error?.response && !/status(?: code)? 422/i.test(String(error.message))) {
      throw error;
    }
    throw new Error(extractCreateError(error));
  }
}

export async function approveRejectSv4Card(payload: {
  EmpID: string;
  ReferenceNumber: string;
  Action: "A" | "R";
  HRemarks: string;
  ApproverUserID: string;
}): Promise<any> {
  const res: any = await Api.post(API_URLS.SV4_CARD.APPROVE_REJECT, payload);
  if (!isApiSuccess(res)) {
    throw new Error(apiMessage(res, payload.Action === "A" ? "Approval failed" : "Rejection failed"));
  }
  return res;
}

/** Same as the app NAVIGATION_MAP key `SV4` — worklist type + text. */
export function isSv4WorklistItem(task: {
  type?: string;
  taskText?: string;
  workItemText?: string;
  subject?: string;
}): boolean {
  const type = String(task?.type || "").toUpperCase().replace(/[\s-]+/g, "_");
  if (
    type === "SV4" ||
    type === "SV4_CARD" ||
    type.includes("SV4") ||
    type.includes("SMART_VALIDATOR")
  ) {
    return true;
  }
  const text = `${task?.taskText || ""} ${task?.workItemText || ""} ${task?.subject || ""}`.toLowerCase();
  return text.includes("sv4") || text.includes("smart validator");
}

/** App opens SV4CardApprovalScreen with `{ requestID }`. */
export function getSv4ApprovalPath(task: { requestID?: string | number; id?: string | number }): string {
  const id = String(task?.requestID || task?.id || "").trim();
  return `/sv4-card/approve/${encodeURIComponent(id)}`;
}
